import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { logger } from '@/lib/logger';
import {
  getPortfolioActionError,
  getSnowflakeActionError,
} from '@/lib/portfolio/errors';
import { useRevokeSnowflakeConnection } from '@/lib/portfolio/hooks/useRevokeSnowflakeConnection';
import { useSnowflakeConnection } from '@/lib/portfolio/hooks/useSnowflakeConnection';
import { useUpdateSnowflakeConnection } from '@/lib/portfolio/hooks/useUpdateSnowflakeConnection';

import { SnowflakeTypeSelectionPicker } from './SnowflakeTypeSelectionPicker';

const RE_ISSUE_WARNING =
  'Saving re-issues the connection credential. The Snowflake connection details do not change.';

const REVOKE_DESCRIPTION = 'Ingestion will stop at the next scheduled poll.';

export interface SnowflakeConnectionManagementProps {
  orgId: number;
}

export function SnowflakeConnectionManagement({
  orgId,
}: SnowflakeConnectionManagementProps) {
  const connectionQuery = useSnowflakeConnection(orgId);
  const updateMutation = useUpdateSnowflakeConnection(orgId);
  const revokeMutation = useRevokeSnowflakeConnection(orgId);
  const connection = connectionQuery.data?.connection ?? null;

  const [selectedTypeNames, setSelectedTypeNames] = useState<string[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);

  // Re-seed local selection whenever the fetched connection's selection
  // changes (initial load, or after a successful save re-syncs the cache).
  // Adjusted during render (not an effect) so the first paint after the
  // connection loads already reflects it — no extra unchecked-then-checked
  // commit for tests/users to race.
  const [seededSelection, setSeededSelection] = useState<string[] | null>(null);
  if (connection && connection.selected_type_names !== seededSelection) {
    setSeededSelection(connection.selected_type_names);
    setSelectedTypeNames(connection.selected_type_names);
  }

  const handleSelectionChange = useCallback((next: string[]) => {
    setSelectedTypeNames(next);
  }, []);

  const isDirty = useMemo(() => {
    const current = connection?.selected_type_names ?? [];
    if (current.length !== selectedTypeNames.length) {
      return true;
    }
    const currentSet = new Set(current);
    return selectedTypeNames.some(name => !currentSet.has(name));
  }, [connection?.selected_type_names, selectedTypeNames]);

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    try {
      await updateMutation.mutateAsync({
        selected_type_names: selectedTypeNames,
      });
      toast.success('Connection updated');
      setIsConfirmOpen(false);
    } catch (err) {
      logger.error(
        "Couldn't update Snowflake connection",
        err instanceof Error ? err : new Error(String(err)),
        { component: 'portfolio', org_id: orgId }
      );
      toast.error(getSnowflakeActionError(err));
    }
  }, [updateMutation, selectedTypeNames, orgId]);

  const handleOpenRevokeConfirm = useCallback(() => {
    setIsRevokeConfirmOpen(true);
  }, []);

  const handleCancelRevokeConfirm = useCallback(() => {
    setIsRevokeConfirmOpen(false);
  }, []);

  const handleConfirmRevoke = useCallback(async () => {
    try {
      await revokeMutation.mutateAsync();
      toast.success('Connection revoked');
      setIsRevokeConfirmOpen(false);
    } catch (err) {
      logger.error(
        "Couldn't revoke Snowflake connection",
        err instanceof Error ? err : new Error(String(err)),
        { component: 'portfolio', org_id: orgId }
      );
      toast.error(getPortfolioActionError(err));
    }
  }, [revokeMutation, orgId]);

  const saveError = updateMutation.isError
    ? getSnowflakeActionError(updateMutation.error)
    : null;

  const revokeError = revokeMutation.isError
    ? getPortfolioActionError(revokeMutation.error)
    : null;

  if (connectionQuery.isLoading) {
    return <LoadingIndicator size='sm' />;
  }

  if (connectionQuery.isError) {
    return (
      <QueryErrorState
        error={connectionQuery.error}
        label="Couldn't load the Snowflake connection"
      />
    );
  }

  if (!connection) {
    return null;
  }

  const isSaveDisabled =
    !isDirty ||
    selectedTypeNames.length === 0 ||
    updateMutation.isPending ||
    revokeMutation.isPending;

  return (
    <div className='mt-6 flex flex-col gap-3 border-t border-[var(--color-border-secondary)] pt-6'>
      <div>
        <h3 className='text-body font-medium text-[var(--color-text-primary)]'>
          Manage connection
        </h3>
        <p className='text-body-small text-[var(--color-text-tertiary)] mt-0.5'>
          Choose what this connection can read.
        </p>
      </div>
      <SnowflakeTypeSelectionPicker
        orgId={orgId}
        value={selectedTypeNames}
        onChange={handleSelectionChange}
        disabled={updateMutation.isPending}
      />
      {selectedTypeNames.length === 0 && (
        <p
          role='alert'
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          Select at least one type to save.
        </p>
      )}
      <div className='flex items-center gap-3'>
        <Button
          color='secondary'
          size='sm'
          isDisabled={isSaveDisabled}
          onClick={handleOpenConfirm}
        >
          Save changes
        </Button>
        <Button
          color='secondary-destructive'
          size='sm'
          isDisabled={updateMutation.isPending}
          onClick={handleOpenRevokeConfirm}
        >
          Revoke connection
        </Button>
      </div>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        variant='warning'
        title='Update Snowflake connection?'
        description={RE_ISSUE_WARNING}
        confirmLabel='Save changes'
        instance='update-snowflake-connection'
        isLoading={updateMutation.isPending}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelConfirm}
      >
        {saveError && (
          <p
            role='alert'
            className='text-body-small text-[var(--color-text-error-primary)]'
          >
            {saveError}
          </p>
        )}
      </ConfirmationDialog>
      <ConfirmationDialog
        isOpen={isRevokeConfirmOpen}
        variant='destructive'
        title='Revoke Snowflake connection?'
        description={REVOKE_DESCRIPTION}
        confirmLabel='Revoke connection'
        instance='revoke-snowflake-connection'
        isLoading={revokeMutation.isPending}
        onConfirm={handleConfirmRevoke}
        onCancel={handleCancelRevokeConfirm}
      >
        <p>
          Data that&apos;s already in your Snowflake account isn&apos;t removed
          right away. A daily cleanup takes care of it.
        </p>
        <p className='mt-2'>Re-approving later will start a fresh export.</p>
        {revokeError && (
          <p
            role='alert'
            className='text-body-small text-[var(--color-text-error-primary)] mt-2'
          >
            {revokeError}
          </p>
        )}
      </ConfirmationDialog>
    </div>
  );
}
SnowflakeConnectionManagement.displayName = 'SnowflakeConnectionManagement';
